/**
 * Пометка на снимке (ПР-03 / ПР-07): штрихи в относительных координатах 0…1
 * впекаются в JPEG до загрузки на сервер.
 */
import { PHOTO_QUALITY, sha256Hex } from './photo-sync';

export type MarkupPoint = { x: number; y: number };
export type MarkupStroke = { points: MarkupPoint[] };

const MAX_STROKES = 40;
const MAX_POINTS = 400;

/** Отбрасывает пустые и обрезает слишком длинные штрихи. */
export function normalizeMarkupStrokes(strokes: MarkupStroke[]): MarkupStroke[] {
  const out: MarkupStroke[] = [];
  for (const stroke of strokes.slice(0, MAX_STROKES)) {
    const points = stroke.points
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .map((p) => ({
        x: Math.min(1, Math.max(0, p.x)),
        y: Math.min(1, Math.max(0, p.y)),
      }))
      .slice(0, MAX_POINTS);
    if (points.length >= 2) out.push({ points });
  }
  return out;
}

/** Рисует штрихи поверх снимка и возвращает новый JPEG с контрольной суммой. */
export async function bakeMarkupOntoPhoto(
  source: Blob,
  strokes: MarkupStroke[],
  quality = PHOTO_QUALITY,
): Promise<{ blob: Blob; sha256: string; size: number }> {
  const normalized = normalizeMarkupStrokes(strokes);
  if (!normalized.length) throw new Error('Нарисуйте пометку на снимке');
  const bitmap = await createImageBitmap(source);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Браузер не смог обработать снимок');
    ctx.drawImage(bitmap, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = Math.max(3, Math.round(Math.min(bitmap.width, bitmap.height) * 0.008));
    for (const stroke of normalized) {
      ctx.beginPath();
      stroke.points.forEach((p, i) => {
        const x = p.x * bitmap.width;
        const y = p.y * bitmap.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob?.size) throw new Error('Не удалось сохранить пометку');
    return { blob, sha256: await sha256Hex(blob), size: blob.size };
  } finally {
    bitmap.close();
  }
}
